import React from 'react';
import * as LucideIcons from 'lucide-react';

interface LucideIconProps {
    name: string;
    className?: string;
    size?: number;
}

// Map icon names to Lucide components
const iconMap: { [key: string]: React.ElementType } = {
    // Navigation icons
    'home': LucideIcons.Home,
    'challenges': LucideIcons.Target,
    'quest': LucideIcons.Compass,
    'playground': LucideIcons.Play,
    'reference': LucideIcons.BookOpen,
    'gamification': LucideIcons.Trophy,
    'leaderboard': LucideIcons.Medal,
    'help': LucideIcons.HelpCircle,
    'profile': LucideIcons.User,
    'admin': LucideIcons.Settings,
    'logout': LucideIcons.LogOut,

    // Rank icons (Novice to Master progression)
    'rank-1': LucideIcons.Sprout,      // Novice
    'rank-2': LucideIcons.Leaf,        // Apprentice
    'rank-3': LucideIcons.TreeDeciduous, // Practitioner
    'rank-4': LucideIcons.Flame,       // Expert
    'rank-5': LucideIcons.Zap,         // Master
    'rank-6': LucideIcons.Crown,       // Grand Master

    // Leaderboard positions
    'position-1': LucideIcons.Crown,
    'position-2': LucideIcons.Award,
    'position-3': LucideIcons.Medal,
    'position-default': LucideIcons.User,

    // Stats icons
    'points': LucideIcons.Coins,
    'missions': LucideIcons.Target,
    'badges': LucideIcons.Award,
    'streak': LucideIcons.Flame,

    // Quest icons
    'quest-spy': LucideIcons.Eye,
    'quest-escape': LucideIcons.Mountain,
    'quest-hunt': LucideIcons.Search,
    'quest-trials': LucideIcons.Sword,
    'quest-time': LucideIcons.Clock,

    // Badge icons
    'badge-newcomer': LucideIcons.Star,
    'badge-first': LucideIcons.Target,
    'badge-pilot': LucideIcons.Plane,
    'badge-ace': LucideIcons.Rocket,
    'badge-commander': LucideIcons.Shield,
    'badge-emperor': LucideIcons.Crown,

    // Action icons
    'logo': LucideIcons.Brain,
    'dark-mode': LucideIcons.Moon,
    'light-mode': LucideIcons.Sun,

    // Applied-practice topic icons (frontend/src/config/topics.ts)
    'topic-basic-prompt-engineering': LucideIcons.Target,
    'topic-writing-editing': LucideIcons.PenLine,
    'topic-summarizing-research': LucideIcons.Library,
    'topic-data-analysis': LucideIcons.BarChart3,
    'topic-coding-debugging': LucideIcons.Code2,
    'topic-tutoring-explaining': LucideIcons.GraduationCap,
    'topic-customer-communication': LucideIcons.MessageCircle,
    'topic-planning-decisions': LucideIcons.Compass,
    'topic-creative-ideation': LucideIcons.Lightbulb,
    'topic-agent-design': LucideIcons.Bot,

    // Reference page category icons
    'ref-core-concepts': LucideIcons.BookOpen,
    'ref-techniques': LucideIcons.Wrench,
    'ref-best-practices': LucideIcons.Sparkles,
    'ref-patterns': LucideIcons.ListChecks,

    // Status / inline UI icons
    'status-locked': LucideIcons.Lock,
    'status-check': LucideIcons.Check,
    'status-pass': LucideIcons.CircleCheck,
    'status-fail': LucideIcons.XCircle,
    'tip': LucideIcons.Lightbulb,
    'graded-info': LucideIcons.BarChart3,
    'search': LucideIcons.Search,
    'celebration': LucideIcons.PartyPopper,
    'star': LucideIcons.Star,

    // Default fallback
    'default': LucideIcons.CircleHelp,
};

const LucideIcon: React.FC<LucideIconProps> = ({ name, className = '', size = 24 }) => {
    // Semantic keys resolve first; a name that's already a real Lucide component
    // name (e.g. "GraduationCap") resolves directly, so data files (achievements,
    // badges) can reference Lucide icons by name without a map entry for each one.
    const IconComponent = iconMap[name] || (LucideIcons as unknown as Record<string, React.ElementType>)[name] || iconMap['default'];

    return (
        <IconComponent
            className={className}
            size={size}
        />
    );
};

export default LucideIcon;
